import { Component, ChangeDetectionStrategy, OnInit, OnDestroy } from '@angular/core';

import { interval, Observable, Subscription } from 'rxjs';

import {
  BackgroundTask,
  BackgroundTaskPaged,
  BackgroundTaskStatus,
  BackgroundTaskStatusConfig,
  FileTypeConfig,
} from './export-job.model';

import { BackgroundTaskService } from './background-task.service';
import { HttpEventType } from '@angular/common/http';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ToastService } from '@app/shared/services/toast/toast.service';

@Component({
  selector: 'app-background-tasks',
  templateUrl: './background-tasks.component.html',
  styleUrls: ['./background-tasks.component.scss'],
  standalone: false,
})
export class BackgroundTasksComponent implements OnInit, OnDestroy {
  readonly Status = BackgroundTaskStatus;
  readonly StatusConfig = BackgroundTaskStatusConfig;
  readonly FileConfig = FileTypeConfig;
  readonly tasks$: Observable<BackgroundTaskPaged>;
  subscriptions: Array<Subscription> = [];
  collapsed = false;
  apiLoader = false;

  constructor(
    public taskService: BackgroundTaskService,
    private router: Router,
    private toastService: ToastService
  ) {
    this.tasks$ = this.taskService.tasks$;
    this.subscriptions.push(
      router.events.subscribe((event: any) => {
        if (event instanceof NavigationEnd) {
          this.taskService.showPanel = false;
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x: Subscription) => {
      x.unsubscribe();
    });

    this.taskService.tempTask = null;
  }

  loadJobs() {
    this.apiLoader = true;
    this.taskService.getMyTask().subscribe({
      next: (res: any) => {
        this.taskService.freshList(res);
        this.apiLoader = false;
      },
      error: (error: any) => {
        this.apiLoader = false;
      },
      complete: () => {
        this.apiLoader = false;
      },
    });
  }

  ngOnInit(): void {
    this.subscriptions.push(
      interval(8000).subscribe((_) => {
        this.loadJobs();
      })
    );

    this.loadJobs();
  }

  trackById(index: number, task: BackgroundTask): string {
    return task.JobId;
  }

  remove(task: BackgroundTask): void {
    this.taskService.deleteFiles([task.JobId]).subscribe((x) => {
      this.loadJobs();
    });
  }

  clearCompleted(): void {
    this.taskService.showPanel = false;
  }

  download(task: BackgroundTask): void {
    if (!task.FileLink) {
      return;
    }

    window.open(task.FileLink, '_blank');
  }

  toggle(): void {
    this.collapsed = !this.collapsed;
  }

  reload() {
    this.loadJobs();
  }

  routeToDownloadManager() {
    this.router.navigate(['/insights/reports/downloadmanager']);
    this.taskService.showPanel = false;
    this.cancelNewTask();
  }

  starkNewTask() {
    const task = this.taskService.tempTask;
    if (task?.FileName && task?.FileName.length > 3) {
      (window as any).DM_FILE_NAME = task?.FileName;
      (window as any).DM_RECORDS_COUNT = task?.RecordsCount;
      (window as any).DM_REPORT_NAME = task?.ReportName;

      const callback: any = task.callback;
      this.taskService.tempTask = null;
      this.taskService.showPanel = false;

      callback({
        'DM-Records-Count': String((window as any).DM_RECORDS_COUNT),
        'DM-File-Name': encodeURIComponent(String((window as any).DM_FILE_NAME)),
        'DM-Report-Name': String((window as any).DM_REPORT_NAME),
      }).subscribe({
        next: (_: any) => {
          this.taskService.showPanel = true; // Runs on success
        },
        error: (err: any) => {
          console.error('Request failed', err); // Runs on error
          // You can choose to set the panel to true here too if needed
        },
      });
    } else {
      this.toastService.error('File name must be greater than 3 characters.');
    }
  }

  retry(task: BackgroundTask) {
    this.taskService.retryAsync([task.JobId]).subscribe((_: any) => {
      this.loadJobs();
    });
  }

  cancelNewTask() {
    this.taskService.showPanel = false;
    this.taskService.tempTask = null;
  }
}
